import { useState } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import LockIcon from '@mui/icons-material/Lock';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import EditIcon from '@mui/icons-material/Edit';
import { featureFlags } from '../utils/featureFlags';

interface BulkActionsToolbarProps {
  selectedCount: number;
  totalCount: number;
  isAllSelected: boolean;
  onSelectAll: () => void;
  onClearAll: () => void;
  onMakePublic: () => Promise<void>;
  onMakePrivate: () => Promise<void>;
  /**
   * Opens the Bulk Edit dialog. The action only renders when this handler is
   * provided AND the `enableBulkEdit` feature flag is on (off in prod).
   */
  onBulkEdit?: () => void;
}

/**
 * Contextual toolbar that appears when catalog items are selected.
 * Provides Select All / Unselect All and bulk Make Public / Make Private actions.
 */
export const BulkActionsToolbar = ({
  selectedCount,
  totalCount,
  isAllSelected,
  onSelectAll,
  onClearAll,
  onMakePublic,
  onMakePrivate,
  onBulkEdit,
}: BulkActionsToolbarProps) => {
  const [loading, setLoading] = useState(false);
  const showBulkEdit = Boolean(onBulkEdit) && featureFlags.isEnabled('enableBulkEdit');

  const handleAction = async (action: () => Promise<void>) => {
    setLoading(true);
    try {
      await action();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1,
        bgcolor: 'action.selected',
        borderBottom: 1,
        borderColor: 'divider',
        flexWrap: 'wrap',
      }}
    >
      <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
        {selectedCount} of {totalCount} selected
      </Typography>

      <Button
        size="small"
        startIcon={isAllSelected ? <DeselectIcon /> : <SelectAllIcon />}
        onClick={isAllSelected ? onClearAll : onSelectAll}
        disabled={loading}
      >
        {isAllSelected ? 'Unselect All' : 'Select All'}
      </Button>

      <Button
        size="small"
        variant="outlined"
        startIcon={loading ? <CircularProgress size={16} /> : <PublicIcon />}
        onClick={() => handleAction(onMakePublic)}
        disabled={loading}
      >
        Make Public
      </Button>

      <Button
        size="small"
        variant="outlined"
        startIcon={loading ? <CircularProgress size={16} /> : <LockIcon />}
        onClick={() => handleAction(onMakePrivate)}
        disabled={loading}
      >
        Make Private
      </Button>

      {showBulkEdit && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={onBulkEdit}
          disabled={loading}
        >
          Bulk Edit
        </Button>
      )}
    </Box>
  );
};
